import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface DeleteOrgDialogProps {
  orgId: string;
  orgName: string;
  onSuccess: () => void;
}

export function DeleteOrgDialog({ orgId, orgName, onSuccess }: DeleteOrgDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleDelete = async () => {
    if (confirmText !== "CONFIRM") {
      toast.error("Please type CONFIRM to proceed");
      return;
    }

    try {
      setLoading(true);

      // Delete all related data first (cascading)
      // The foreign key constraints should handle most of this, but we'll be explicit

      // 1. Delete all jobs and their related data
      const { error: jobsError } = await supabase
        .from('jobs')
        .delete()
        .eq('org_id', orgId);

      if (jobsError) throw jobsError;

      // 2. Remove users from org (don't delete users, just unlink them)
      const { error: usersError } = await supabase
        .from('profiles')
        .update({ org_id: null })
        .eq('org_id', orgId);

      if (usersError) throw usersError;

      // 3. Delete user roles for this org
      const { error: rolesError } = await supabase
        .from('user_roles')
        .delete()
        .eq('org_id', orgId);

      if (rolesError) throw rolesError;

      // 4. Finally delete the organization
      const { error: orgError } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId);

      if (orgError) throw orgError;

      toast.success(`Organization "${orgName}" has been permanently deleted`);
      setOpen(false);
      setConfirmText("");
      onSuccess();
    } catch (error: any) {
      console.error('Failed to delete organization:', error);
      toast.error(`Failed to delete organization: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" className="gap-2">
          <Trash2 className="w-4 h-4" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Organization</AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p className="font-semibold text-foreground">
              Are you absolutely sure you want to permanently delete <span className="text-destructive">{orgName}</span>?
            </p>
            <p>This action will:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Delete all jobs and associated data</li>
              <li>Remove all user associations</li>
              <li>Delete all organization records</li>
            </ul>
            <p className="text-destructive font-medium">This action cannot be undone!</p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="confirm-delete">
            Type <strong>CONFIRM</strong> to proceed
          </Label>
          <Input
            id="confirm-delete"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type CONFIRM"
            disabled={loading}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button 
            variant="destructive" 
            onClick={handleDelete}
            disabled={loading || confirmText !== "CONFIRM"}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete Permanently
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
